/**
 * @license
 * Copyright Google Inc. All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */


import {Component, ChangeDetectionStrategy, Input, ViewEncapsulation} from '@angular/core';
/**
 * <md-progress-bar> component.
 */
export class MdProgressBar {
/**
 * Color of the progress bar.
 */
color: 'primary' | 'accent' | 'warn' = 'primary';
private _value: number = 0;
/**
 * Value of the progressbar. Defaults to zero. Mirrored to aria-valuenow.
 * @return {?}
 */
get value() { return this._value; }
/**
 * @param {?} v
 * @return {?}
 */
set value(v: number) { this._value = clamp(v || 0); }
private _bufferValue: number = 0;
/**
 * Buffer value of the progress bar. Defaults to zero.
 * @return {?}
 */
get bufferValue() { return this._bufferValue; }
/**
 * @param {?} v
 * @return {?}
 */
set bufferValue(v: number) { this._bufferValue = clamp(v || 0); }
/**
 * Mode of the progress bar.
 * 
 * Input must be one of these values: determinate, indeterminate, buffer, query, defaults to
 * 'determinate'.
 * Mirrored to mode attribute.
 */
mode: 'determinate' | 'indeterminate' | 'buffer' | 'query' = 'determinate';
/**
 * Gets the current transform value for the progress bar's primary indicator.
 * @return {?}
 */
_primaryTransform() {
    let /** @type {?} */ scale = this.value / 100;
    return {transform: `scaleX(${scale})`};
  }
/**
 * Gets the current transform value for the progress bar's buffer indicator.  Only used if the
 * progress mode is set to buffer, otherwise returns an undefined, causing no transformation.
 * @return {?}
 */
_bufferTransform() {
    if (this.mode == 'buffer') {
      let /** @type {?} */ scale = this.bufferValue / 100;
      return {transform: `scaleX(${scale})`};
    }
  }
static decorators: DecoratorInvocation[] = [
{ type: Component, args: [{
  moduleId: module.id,
  selector: 'md-progress-bar, mat-progress-bar',
  host: {
    'role': 'progressbar',
    'aria-valuemin': '0',
    'aria-valuemax': '100',
    '[attr.aria-valuenow]': 'value',
    '[attr.mode]': 'mode',
    '[class.mat-primary]': 'color == "primary"',
    '[class.mat-accent]': 'color == "accent"',
    '[class.mat-warn]': 'color == "warn"',
    'class': 'mat-progress-bar',
  },
  templateUrl: 'progress-bar.html',
  styleUrls: ['progress-bar.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  preserveWhitespaces: false,
}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
];
static propDecorators: {[key: string]: DecoratorInvocation[]} = {
'color': [{ type: Input },],
'value': [{ type: Input },],
'bufferValue': [{ type: Input },],
'mode': [{ type: Input },],
};
}

function MdProgressBar_tsickle_Closure_declarations() {
/** @type {?} */
MdProgressBar.decorators;
/**
 * @nocollapse
 * @type {?}
 */
MdProgressBar.ctorParameters;
/** @type {?} */
MdProgressBar.propDecorators;
/**
 * Color of the progress bar.
 * @type {?}
 */
MdProgressBar.prototype.color;
/** @type {?} */
MdProgressBar.prototype._value;
/** @type {?} */
MdProgressBar.prototype._bufferValue;
/**
 * Mode of the progress bar.
 * 
 * Input must be one of these values: determinate, indeterminate, buffer, query, defaults to
 * 'determinate'.
 * Mirrored to mode attribute.
 * @type {?}
 */
MdProgressBar.prototype.mode;
}

/**
 * Clamps a value to be between two numbers, by default 0 and 100.
 * @param {?} v
 * @param {?=} min
 * @param {?=} max
 * @return {?}
 */
function clamp(v: number, min = 0, max = 100) {
  return Math.max(min, Math.min(max, v));
}

interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
